import { Suspense, type FC, type ReactNode } from "react";
import { FullCsticId, type LinearCsticValue, type LinearSubstep } from "~/features/configurator/Linear/models";
import { useSelector } from "react-redux";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import { Col, Row } from "~/common/components/Layout";
import { selectKmatAmount, selectSubKmatCstics } from "~/features/configurator/Linear/redux/selectors";
import { CSTIC_PART_SIZING_TYPE_MANUAL } from "~/features/configurator/Linear/constants";
import { LinearSectionTitle } from "~/features/configurator/Linear/components/LinearSectionTitle";
import { type RootState } from "~/app/store";
import { PieceLengthInput } from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/components/PieceLengthInput";
import classNames from "classnames";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { Card } from "~/features/configurator/Linear/components/Card";
import { makeMinMaxLabel } from "~/features/configurator/Linear/hooks/useALARLogic";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import type { KmatType } from "~/features/configurator/Linear/kmatType";
import {
  type AlarHelperTextFactory,
  ExtraConfig,
  HelperCstics,
  InputCstics,
} from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/constants";
import { Box, CircularProgress, Stack, styled } from "~/common/mui";
import type { ResolvedCsticDefinition } from "~/features/configurator/common/v2/StepDefinition";
import { KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { SimpleCsticNumberField } from "~/features/configurator/Shaft/components/SimpleCsticNumberField";
import { SimpleCsticSelectField } from "~/features/configurator/Shaft/components/SimpleCsticSelectField";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { getKmatCstics } from "~/features/configurator/common/v2/helper";
import {
  ConfiguratorFamily,
  ConfiguratorType,
  getConfiguratorFamilyFromProductCode,
  getConfiguratorTypeFromProductCode,
} from "~/domains/configurator/types/ConfiguratorType";
import { KLLTGuidewaySetHeader } from "./components/KLLTUnitHeader";
import { KUEMGuidewaySetHeader } from "./components/KUEMUnitHeader";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const KLLTGuidewaySetStep = ({ def, ...props }: Props) => {
  const kmatAmount = useSelector(selectKmatAmount(def.kmatType));

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          {Array.from({ length: kmatAmount }, (_, index) => (
            <Col size={4} key={index}>
              <SingleGuidewayTile
                index={index}
                def={def}
                kmatType={def.kmatType}
                railAmount={kmatAmount}
                railIndex={index}
              />
            </Col>
          ))}
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default KLLTGuidewaySetStep;

interface TileProps {
  index: number;
  kmatType: KmatType;
  def: LinearSubstep;
  railAmount: number;
  railIndex: number;
}

const SingleGuidewayTile = (props: TileProps) => {
  const { def, index, kmatType, railAmount, railIndex } = props;

  const substepType = def.type;
  const cstics = useSelector(selectSubKmatCstics(kmatType, index));

  const formatValue = (val: string | undefined) => {
    return val ? NF.getDefaultFormatter().format(+val) : "N/A";
  };
  const { helper: helperCstics, input: inputCstics } = getKmatCstics(def, index);

  const makeCstiId = (name: string) => FullCsticId({ key: name, index, type: kmatType });
  const getHelperName = (name: string) => helperCstics[name]?.name;

  const xmlT = useXmlConfiguratorTranslations().t!;
  const { t } = useTranslation();

  const minCsticName = getHelperName(HelperCstics.totalLengthMin);
  const minLength = cstics[minCsticName]?.value;
  const maxCsticName = getHelperName(HelperCstics.totalLengthMax);
  const maxLength = cstics[maxCsticName]?.value;
  const lengthHelperLabel = `${formatValue(minLength)}mm - ${formatValue(maxLength)}mm`;

  const totalLengthDef = inputCstics[InputCstics.TotalLength];

  const partAmountCsticDef = inputCstics[InputCstics.partAmount];

  const partSizingCsticDef = inputCstics[InputCstics.partSizingType];
  const partSizingCstic = cstics[partSizingCsticDef.name];
  const isCustomPieceSizes = partSizingCstic?.value === CSTIC_PART_SIZING_TYPE_MANUAL;

  const renderDropdownEntry = (value: LinearCsticValue) => <LanguageAwareExternalContent content={value.label} />;

  const alarVisible = inputCstics[InputCstics.aL].visible && inputCstics[InputCstics.aR].visible;

  const makeGetValue = (source: Record<string, ResolvedCsticDefinition>) => (csticName: string) => {
    const stringVal = cstics[source[csticName]?.name]?.value;
    if (stringVal) {
      return +stringVal;
    } else {
      return undefined;
    }
  };
  const getHelperValue = makeGetValue(helperCstics);
  const getValue = makeGetValue(inputCstics);

  const alarHelperData = [
    {
      recommendedMin: getHelperValue(HelperCstics.alRecommendedMin),
      recommendedMax: getHelperValue(HelperCstics.alRecommendedMax),
      hardMin: 0,
      hardMax: getHelperValue(HelperCstics.alHardMax),
      value: getValue(InputCstics.aL) ?? null,
      t,
    },
    {
      recommendedMin: getHelperValue(HelperCstics.arRecommendedMin),
      recommendedMax: getHelperValue(HelperCstics.arRecommendedMax),
      hardMin: 0,
      hardMax: getHelperValue(HelperCstics.arHardMax),
      value: getValue(InputCstics.aR) ?? null,
      t,
    },
  ];

  const helperTextFactory = def.extraConfig[ExtraConfig.AlarHelperTextFactory] as AlarHelperTextFactory;
  const alHelperText = helperTextFactory(alarHelperData[0]);
  const arHelperText = helperTextFactory(alarHelperData[1]);

  let lengthDecimals;
  let alarDecimals;
  const productCode = useSelector((state: RootState) => state.linearConfigurator.productCode);

  if (productCode === KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE) {
    lengthDecimals = 1;
    alarDecimals = 2;
  } else {
    lengthDecimals = 0;
    alarDecimals = 1;
  }

  let showNotRecommendedWarning = (def.extraConfig[ExtraConfig.showNotRecommendedWarning] ?? false) as boolean;
  if (showNotRecommendedWarning) {
    showNotRecommendedWarning = alarHelperData.some((helperData) => {
      if (!helperData.value || !helperData.recommendedMin || !helperData.recommendedMax) {
        return false;
      }
      return helperData.value < helperData.recommendedMin || helperData.value > helperData.recommendedMax;
    });
  }

  const configuratorFamily = getConfiguratorFamilyFromProductCode(productCode);
  const configuratorType = getConfiguratorTypeFromProductCode(productCode);

  return (
    <Card disableHover={true}>
      <StyledStack gap={2}>
        <Suspense
          fallback={
            <Box sx={{ width: "100%", aspectRatio: "659/359", display: "grid", placeItems: "center" }}>
              <CircularProgress size={48} color="primary" />
            </Box>
          }
        >
          {configuratorFamily === ConfiguratorFamily.KLLT && configuratorType === ConfiguratorType.System && (
            <KLLTGuidewaySetHeader kmatType={kmatType} railAmount={railAmount} railIndex={railIndex} />
          )}
          {configuratorFamily === ConfiguratorFamily.KUEM && configuratorType === ConfiguratorType.System && (
            <KUEMGuidewaySetHeader />
          )}
        </Suspense>

        {def.extraTexts["upperMessage"] && (
          <Alert severity={"info"}>
            <LanguageAwareExternalContent content={def.extraTexts["upperMessage"]} />
          </Alert>
        )}

        <LinearSection label={xmlT(def.extraTexts["lengthName"])} info={def.extraTexts["lengthInfo"]}>
          <SimpleCsticNumberField
            csticId={makeCstiId(totalLengthDef.name)}
            substepType={substepType}
            disabled={totalLengthDef.disabled}
            helperLabel={lengthHelperLabel}
            required={totalLengthDef.required}
            decimalScale={lengthDecimals}
          />
        </LinearSection>

        <Alert severity={"info"} className={"pb-3"}>
          <LanguageAwareExternalContent content={def.extraTexts["lowerMessage"]} />
        </Alert>

        {alarVisible && (
          <>
            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["alarName"]}
                  className={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["alarInfo"]}
            >
              <Row>
                <Col size={6}>
                  <SimpleCsticNumberField
                    useCsticLabel
                    csticId={makeCstiId(inputCstics[InputCstics.aL].name)}
                    substepType={substepType}
                    disabled={isCustomPieceSizes}
                    required={inputCstics[InputCstics.aL].required}
                    decimalScale={alarDecimals}
                    helperLabel={alHelperText}
                  />
                </Col>

                <Col size={6}>
                  <SimpleCsticNumberField
                    useCsticLabel
                    csticId={makeCstiId(inputCstics[InputCstics.aR].name)}
                    substepType={substepType}
                    disabled={isCustomPieceSizes || inputCstics[InputCstics.aR].disabled}
                    required={inputCstics[InputCstics.aR].required}
                    decimalScale={alarDecimals}
                    helperLabel={arHelperText}
                  />
                </Col>
              </Row>
              {showNotRecommendedWarning && (
                <Alert severity={"info"} className={"mt-3"}>
                  <LanguageAwareExternalContent content={def.extraTexts["alarWarningMessage"]} />
                </Alert>
              )}
            </LinearSection>

            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["partsSectionName"]}
                  className={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["partsSectionInfo"]}
            >
              <Row>
                <Col size={6}>
                  <SimpleCsticSelectField
                    useCsticLabel
                    csticId={makeCstiId(partSizingCsticDef.name)}
                    disabled={partSizingCsticDef.disabled}
                    substepType={substepType}
                    renderValue={renderDropdownEntry}
                  />
                </Col>

                <Col size={6}>
                  <SimpleCsticSelectField
                    useCsticLabel
                    csticId={makeCstiId(partAmountCsticDef.name)}
                    disabled={partAmountCsticDef.disabled}
                    substepType={substepType}
                    renderValue={renderDropdownEntry}
                  />
                </Col>
              </Row>
              {PIECE_INPUTS.filter((csticName) => inputCstics[csticName]?.visible).map((csticName, partIndex) => (
                <PieceLengthInput
                  key={csticName}
                  cstics={cstics}
                  kmatIndex={index}
                  pieceIndex={partIndex}
                  disabled={inputCstics[csticName].disabled}
                  isSecondLast={false}
                  isLast={partIndex === PIECE_INPUTS.length - 1}
                  substep={substepType}
                  makeMinMaxLabelFormatter={makeMinMaxLabel}
                />
              ))}
            </LinearSection>
          </>
        )}
      </StyledStack>
    </Card>
  );
};

const PIECE_INPUTS = [
  InputCstics.partOne,
  InputCstics.partTwo,
  InputCstics.partThree,
  InputCstics.partFour,
  InputCstics.partFive,
];

const StyledStack = styled(Stack)({
  padding: "16px",
});

const LinearSection: FC<{ label: ReactNode; info?: string; className?: string; children?: ReactNode }> = ({
  children,
  label,
  info,
  className,
}) => {
  return (
    <div className={classNames("mb-30", className)}>
      <LinearSectionTitle className={"mt-0 mb-2"} info={info}>
        {label}
      </LinearSectionTitle>
      {children}
    </div>
  );
};
